
import { defineComponent } from "vue";
import CustomTextInput from "../components/inputs/CustomTextInput.vue";
import CustomSwitch from "../components/inputs/CustomSwitch.vue";
import CustomLabel from "../components/inputs/CustomLabel.vue";
import CustomSelect from "../components/inputs/CustomSelect.vue";
import KeyValueRow from "../components/layout/KeyValueRow.vue";
import Card from "../components/layout/Card.vue";
import AboutCardContent from "../components/AboutCardContent.vue";

import { competitionIdToReadableName, convertDateToString } from "../helpers/formatting";
import { colorThemes } from "../components/layout/colorThemes";

import * as getScheduleHelper from "../schedule/get-schedule-helper";

import i18nMessagesRaw from "../locales/_index";
import { IRecursiveObject } from "../types";
interface Ii18nMessages {
  [language: string]: IRecursiveObject<string>;
}
const i18nMessages: Ii18nMessages = i18nMessagesRaw;

export default defineComponent({
  name: "Settings",
  components: {
    CustomTextInput,
    CustomSwitch,
    CustomLabel,
    KeyValueRow,
    Card,
    CustomSelect,
    AboutCardContent,
  },
  computed: {
    switchDrawerOnRight() {
      return this.$store.state.settings.drawerSide === "right";
    },
    colorThemeOptions(): Array<{ text: string; value: string }> {
      return colorThemes.map((theme) => ({ text: this.tc("themes." + theme), value: theme }));
    },
    scheduleLastUpdateTime() {
      return convertDateToString(new Date(this.$store.state.schedule.timestamp));
    },
    scheduleTeamsPerCompetition() {
      const teamsPerCompetition: { [competition: string]: number } = {};
      for (const team of this.$store.state.schedule.teams) {
        if (team.competition in teamsPerCompetition) {
          teamsPerCompetition[team.competition] += 1;
        } else {
          teamsPerCompetition[team.competition] = 1;
        }
      }
      return Object.entries(teamsPerCompetition).reduce((text, competitionWithTeamCount) => {
        return (
          text + (text === "" ? "" : ", ") + `${competitionWithTeamCount[1]} (${competitionIdToReadableName(competitionWithTeamCount[0])})`
        );
      }, "");
    },
  },
  methods: {
    tc(key: string): string {
      return this.$t("settings." + key);
    },
    onInputDrawerPosition(rightSide: boolean) {
      this.$store.commit("setDrawerSide", rightSide ? "right" : "left");
    },
    getLocaleOptionName(locale: string): string {
      if (locale in i18nMessages && "languageName" in i18nMessages[locale]) {
        return `${i18nMessages[locale].languageName} (${locale})`;
      }
      return locale;
    },
    setSettingFunction(name: string): (value: string) => void {
      return (value: string) => this.$store.commit("setSetting", { name, value });
    },
    displayArrayValues(array: string[]): string {
      return array.reduce((values, value) => {
        return values + (values === "" ? "" : ", ") + value;
      }, "");
    },
    async updateScheduleData(): Promise<void> {
      const baseUrl = `${this.$store.state.settings.submitHost}/schedule/json/`;
      const data: string[] = [];
      const filenames = getScheduleHelper.jsonFiles;
      try {
        for (const file of filenames) {
          const response = await fetch(baseUrl + file);
          data.push(await response.text());
        }
        const computedData = getScheduleHelper.getComputedData(data, filenames);
        const variableNameFilenameMapping = {
          arenas: "arenas.json",
          competitions: "competitions.json",
          events: "events.json",
          runs: "scheduled-runs.json",
          teams: "teams.json",
        };
        // eslint-disable-next-line
        const payloadForUpdate: any = {
          ...computedData,
          timestamp: Date.now(),
        };
        for (const variableName in variableNameFilenameMapping) {
          payloadForUpdate[variableName] = JSON.parse(
            data[
              filenames.findIndex(
                (name) =>
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  name === variableNameFilenameMapping[variableName]
              )
            ]
          );
        }
        this.$store.commit("updateSchedule", payloadForUpdate);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        console.log("error", err.message);
      }
    },
  },
  mounted() {
    this.$watch(
      () => this.$i18n.locale,
      () => {
        this.$store.commit("setLanguage", this.$i18n.locale);
      }
    );
  },
});
